// Browser → Cloudflare R2 upload helper.
//
// R2 is the sole storage backend. Public buckets are served via
// `https://cdn.podgo.io/<bucket>/<path>`; private buckets use signed S3 GETs
// minted by `get-raw-audio-url` / `sign-private-batch`.

import { supabase } from "@/integrations/supabase/client";
import { cdnUrl } from "@/lib/cdnUrl";

export type R2Bucket =
  | "studio-photos"
  | "bug-screenshots"
  | "show-covers"
  | "avatars"
  | "featured-logos"
  | "blog-images"
  | "episode-media"
  | "episode-media-raw"
  | "episode-drafts"
  | "resumes"
  | "voice-samples";

export interface R2UploadParams {
  bucket: R2Bucket;
  path: string;
  file: Blob;
  contentType?: string;
  /** Progress 0..100. When provided, upload runs through XHR. */
  onProgress?: (percent: number) => void;
  /** Abort the upload. When provided, upload runs through XHR. */
  signal?: AbortSignal;
}

export interface R2UploadResult {
  publicUrl: string;
  path: string;
  bucket: string;
}

interface SignResponse {
  uploadUrl: string;
  method: "PUT";
  headers: Record<string, string>;
  publicUrl: string;
  bucket: string;
  path: string;
  expiresAt: number;
}

async function invokeSign(bucket: R2Bucket, path: string, contentType: string) {
  // Force any pending token refresh to complete before invoke()'s internal
  // fetch reads the session. Long uploads can outlive the access token, and a
  // silently expired JWT surfaces as a FunctionsFetchError, not a 401.
  try {
    await supabase.auth.getSession();
  } catch {
    /* non-fatal: invoke will surface the real error */
  }
  return supabase.functions.invoke("storage-sign-upload", {
    body: { bucket, path, contentType },
  });
}

async function signUpload(
  bucket: R2Bucket,
  path: string,
  contentType: string,
): Promise<SignResponse> {
  let { data, error } = await invokeSign(bucket, path, contentType);

  // Retry once on FunctionsFetchError (underlying fetch rejected — network
  // hiccup, token refresh race, backgrounded tab). Do NOT retry on HTTP
  // errors (FunctionsHttpError) — those are deterministic.
  if (error && (error as { name?: string }).name === "FunctionsFetchError") {
    await new Promise((r) => setTimeout(r, 750));
    ({ data, error } = await invokeSign(bucket, path, contentType));
  }

  if (error) {
    const name = (error as { name?: string }).name || "Error";
    const message = (error as { message?: string }).message || String(error);
    throw new Error(`R2 sign failed (${name}): ${message}`);
  }
  const signed = data as SignResponse;
  if (!signed?.uploadUrl) throw new Error("R2 sign response missing uploadUrl");
  return signed;
}

function putWithXhr(
  url: string,
  contentType: string,
  body: Blob,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    if (signal) {
      if (signal.aborted) {
        reject(new DOMException("Upload cancelled", "AbortError"));
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`R2 upload failed (${xhr.status}): ${xhr.responseText}`));
    };
    xhr.onerror = () => reject(new Error("Network error during R2 upload"));
    xhr.onabort = () => reject(new DOMException("Upload cancelled", "AbortError"));
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.send(body);
  });
}

export async function uploadToR2(params: R2UploadParams): Promise<R2UploadResult> {
  const contentType =
    params.contentType ||
    (params.file as File).type ||
    "application/octet-stream";

  const signed = await signUpload(params.bucket, params.path, contentType);

  if (params.onProgress || params.signal) {
    await putWithXhr(
      signed.uploadUrl,
      contentType,
      params.file,
      params.onProgress,
      params.signal,
    );
  } else {
    const res = await fetch(signed.uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: params.file,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`R2 upload failed: ${res.status} ${text}`);
    }
  }

  return {
    publicUrl: signed.publicUrl,
    path: signed.path,
    bucket: signed.bucket,
  };
}

/**
 * Build the public CDN URL for an R2 object without an upload round-trip.
 * Used for read-only paths that previously called `supabase.storage.getPublicUrl()`.
 */
export function r2PublicUrl(bucket: R2Bucket, path: string): string {
  return cdnUrl(bucket, path);
}
